/**
 * DemoSiteBanner - CTA banner for demo site pages
 *
 * Matches the Svelte version from Humblytics-web
 * Shows when viewing demo data and prompts to add a website
 */

import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/lib/contexts/AuthContext";
import { getAllProperties } from "@/lib/api/api";
import { DEMO_PROPERTY_ID } from "@/lib/constants";
import PlanExplorerDialog from "@/components/dashboard/hub/PlanExplorerDialog";

interface DemoSiteBannerProps {
  propertyId?: string | null;
  onDismiss?: () => void;
}
import { HugeiconsIcon } from "@hugeicons/react"
import { Cancel01Icon } from "@hugeicons/core-free-icons"

export default function DemoSiteBanner({
  propertyId,
  onDismiss,
}: DemoSiteBannerProps) {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [hasAddedWebsite, setHasAddedWebsite] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showPricingModal, setShowPricingModal] = useState(false);

  useEffect(() => {
    const checkWebsiteStatus = async () => {
      if (!token || propertyId !== DEMO_PROPERTY_ID) {
        setIsLoading(false);
        return;
      }

      try {
        // Check if user has real sites (non-demo) and subscription status
        const propertiesResponse = await getAllProperties(token);
        const properties = propertiesResponse?.properties || [];
        const realSites = properties.filter(
          (p: any) => p.propertyId !== DEMO_PROPERTY_ID
        );

        setHasAddedWebsite(realSites.length > 0);

        // Check if user has activated a subscription (not just local trial)
        setHasSubscription(propertiesResponse?.subscriptionExists ?? false);
      } catch (error) {
        console.error("Error checking website status:", error);
      } finally {
        setIsLoading(false);
      }
    };

    checkWebsiteStatus();
  }, [token, propertyId]);

  const handleDismiss = () => {
    setIsDismissed(true);
    if (onDismiss) {
      onDismiss();
    }
  };

  // Don't show if:
  // - Not demo site
  // - Loading
  // - Dismissed
  // - Not viewing demo property
  if (isLoading || isDismissed || propertyId !== DEMO_PROPERTY_ID) {
    return null;
  }

  // Determine what CTA to show based on user status
  const showCTA = !hasAddedWebsite || (hasAddedWebsite && !hasSubscription);
  const ctaText = !hasAddedWebsite ? "Add Your Website" : "Start free trial";
  const ctaAction = !hasAddedWebsite
    ? () => navigate("/add-website")
    : () => setShowPricingModal(true);

  return (
    <>
      <Card className="overflow-hidden border-border/60 mb-3">
        <div className="flex items-center justify-between p-3 bg-muted/30 gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Small dashboard preview icon/image */}
            <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-md border border-border bg-card shadow-sm overflow-hidden flex items-center justify-center">
              <svg
                className="w-6 h-6 text-primary/60"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <p className="text-sm font-medium text-foreground/70 leading-tight">
              <span className="font-semibold text-foreground">Demo Site:</span>{" "}
              You're exploring sample data.
              {!hasAddedWebsite && (
                <span className="text-muted-foreground">
                  {" "}
                  Add your website to activate your 14-day free trial and start
                  tracking real visitors.
                </span>
              )}
              {hasAddedWebsite && !hasSubscription && (
                <span className="text-muted-foreground">
                  {" "}
                  Start your free trial to track real visitor data on your
                  sites.
                </span>
              )}
            </p>
          </div>
          {showCTA && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                size="sm"
                onClick={ctaAction}
                className="bg-primary-orange hover:bg-orange-600 text-white text-xs font-semibold shadow-sm"
              >
                {ctaText}
              </Button>
              {onDismiss && (
                <button
                  onClick={handleDismiss}
                  className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded hover:bg-muted flex-shrink-0"
                  aria-label="Dismiss banner"
                >
                  <HugeiconsIcon icon={Cancel01Icon} size={16} />
                </button>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Pricing Modal */}
      <PlanExplorerDialog
        open={showPricingModal}
        onOpenChange={setShowPricingModal}
      />
    </>
  );
}
